import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Skeleton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField
} from '@mui/material';

import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Event as EventIcon
} from '@mui/icons-material';

import apiService from '../services/api';

import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { ScatterChart, ScatterSeries } from '@mui/x-charts/ScatterChart';
import { ChartsTooltip, useItemTooltip } from '@mui/x-charts/ChartsTooltip';
import { styled } from '@mui/material/styles';
import { lineElementClasses, areaElementClasses } from '@mui/x-charts';
import { blueberryTwilightPalette } from '@mui/x-charts/colorPalettes';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { DateField } from '@mui/x-date-pickers/DateField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ruLocale from 'date-fns/locale/ru';

import dataMaterial from '../data/material.json';
import dataCharts from '../data/x-charts.json';
import dataGrid from '../data/x-data-grid.json';
import dataPickers from '../data/x-date-pickers.json';
import transistorCPUdata from '../data/x-scatter-data.js';

const IntlAbsolute = new Intl.NumberFormat('en-US');
const valueFormatter = (value = null) => {
  if (value !== null) {
    return IntlAbsolute.format(value);
  }
  return value;
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}).format;

const IntlNumber = new Intl.NumberFormat('en-US', { notation: 'compact' });
const IntlPercent = new Intl.NumberFormat('en-US', { style: 'percent' });
const percentValueFormatter = (value = null) => {
  if (value !== null) {
    return IntlPercent.format(value / 100);
  }
  return value;
};

const shortMonthYearFormatter = (date) => {
  if (date.getDate() !== 1) {
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  }
  if (date.getMonth() !== 0) {
    return date.toLocaleString('en-US', { month: 'short' });
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
  });
};

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

const DashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Состояние для данных дашборда
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsers: 0,
    blockedUsers: 0,
    emailUsers: 0,
    phoneUsers: 0
  });

  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Получаем список пользователей
        const response = await apiService.users.getAll(1, 5);

        if (response.data.success) {
          const users = response.data.data.users;
          const total = response.data.data.total;

          // Рассчитываем статистику
          const blocked = users.filter(user => user.isBlocked).length;
          const email = users.filter(user => user.authMethod === 'email').length;
          const phone = users.filter(user => user.authMethod === 'phone').length;

          // Устанавливаем данные
          setStats({
            totalUsers: total,
            newUsers: users.length,
            blockedUsers: blocked,
            emailUsers: email,
            phoneUsers: phone
          });

          setRecentUsers(users);
        }
      } catch (error) {
        console.error('Ошибка загрузки данных дашборда:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);



  const statCardData = [
    {
      title: 'Users',
      value: '144k',
      data: [200, 24, 220, 260, 240, 380, 100, 240, 280, 240],
    },
    {
      title: 'Conversions',
      value: '325k',
      data: [1640, 1250, 970, 1130, 1050, 900, 720, 1080, 900, 450],
    },
    {
      title: 'Event count',
      value: '200k',
      data: [500, 400, 510, 530, 520, 600, 530, 520, 510, 730],
    },
  ];

  /** */

  function DownloadDemo() {
    const theme = useTheme();
    const [selectedPackage, setSelectedPackage] = useState('registrations');
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
    const [endDate, setEndDate] = useState(new Date());
    const [analyticsData, setAnalyticsData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Доступные пакеты данных (типы аналитики)
    const packages = {
      registrations: {
        label: 'Регистрации пользователей',
        series: [
          { id: 'successful', label: 'Успешные', dataKey: 'successfulRegistrations', area: true, color: theme.palette.success.main },
          { id: 'failed', label: 'Неуспешные', dataKey: 'failedRegistrations', area: true, color: theme.palette.error.main },
          { id: 'total', label: 'Всего', dataKey: 'totalRegistrations', area: true, color: theme.palette.primary.main }
        ]
      },
      eventsPerUser: {
        label: 'События на пользователя',
        series: [
          { id: 'events', label: 'Всего событий', dataKey: 'totalEvents', area: true },
          { id: 'eventsAvg', label: 'Событий на пользователя', dataKey: 'eventsPerUser', area: true }
        ]
      },
      activeSessions: {
        label: 'Активные сеансы (MAU)',
        series: [
          { id: 'uniqueUsers', label: 'Уник. пользователи', dataKey: 'uniqueUsers', area: true },
          { id: 'totalSessions', label: 'Всего сеансов', dataKey: 'totalSessions', area: true }
        ]
      },
      scans: {
        label: 'Сканы товаров',
        series: [
          { id: 'scansSuccessful', label: 'Успешные сканы', dataKey: 'successfulScans', area: true },
          { id: 'scansFailed', label: 'Неуспешные сканы', dataKey: 'failedScans', area: true },
          { id: 'scansTotal', label: 'Всего сканов', dataKey: 'totalScans', area: true },
          { id: 'scansAvg', label: 'Сканов на пользователя', dataKey: 'scansPerUser', area: true }
        ]
      },
      tasks: {
        label: 'Созданные задачи',
        series: [
          { id: 'tasksSuccessful', label: 'Успешные задачи', dataKey: 'successfulTasks', area: true },
          { id: 'tasksFailed', label: 'Неуспешные задачи', dataKey: 'failedTasks', area: true },
          { id: 'tasksTotal', label: 'Всего задач', dataKey: 'totalTasks', area: true },
          { id: 'tasksAvg', label: 'Задач на пользователя', dataKey: 'tasksPerUser', area: true }
        ]
      },
      chatRequests: {
        label: 'Запросы в чат',
        series: [
          { id: 'chatSuccessful', label: 'Успешные запросы', dataKey: 'successfulChatRequests', area: true },
          { id: 'chatFailed', label: 'Неуспешные запросы', dataKey: 'failedChatRequests', area: true },
          { id: 'chatTotal', label: 'Всего запросов', dataKey: 'totalChatRequests', area: true },
          { id: 'chatAvg', label: 'Запросов на пользователя', dataKey: 'chatRequestsPerUser', area: true },
          { id: 'chatWithPhoto', label: 'С фото', dataKey: 'chatRequestsWithPhoto', area: true },
          { id: 'chatWithoutPhoto', label: 'Без фото', dataKey: 'chatRequestsWithoutPhoto', area: true }
        ]
      },
      cardTransitions: {
        label: 'Переходы по карточкам',
        series: [
          { id: 'fromHome', label: 'Из дома', dataKey: 'fromHome', area: true },
          { id: 'fromResults', label: 'Из результатов', dataKey: 'fromResults', area: true },
          { id: 'averagePerUser', label: 'На пользователя', dataKey: 'transitionsPerUser', area: true }
        ]
      }
    };

    // Загрузка данных аналитики при изменении параметров
    useEffect(() => {
      const fetchAnalytics = async () => {
        setLoading(true);
        try {
          const formatStartDate = startDate ? startDate.toISOString().split('T')[0] : '';
          const formatEndDate = endDate ? endDate.toISOString().split('T')[0] : '';

          const response = await apiService.analytics.getAnalytics(formatStartDate, formatEndDate);

          if (response.data.success) {
            console.log(response.data.data)
            setAnalyticsData(response.data.data);
          }
        } catch (error) {
          console.error('Ошибка загрузки аналитики:', error);
          setAnalyticsData([]);
        } finally {
          setLoading(false);
        }
      };

      if (startDate && endDate) {
        fetchAnalytics();
      }
    }, [selectedPackage, startDate, endDate]);

    const selectedData = packages[selectedPackage];

    return (
      <Stack spacing={1} direction="column" sx={{ height: '100%', minHeight: 0 }}>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Select
            size="small"
            sx={{ width: '100%', padding: '0.5rem' }}
            value={selectedPackage}
            onChange={(event) => setSelectedPackage(event.target.value)}
          >
            {Object.entries(packages).map(([key, pkg]) => (
              <MenuItem key={key} value={key}>
                {pkg.label}
              </MenuItem>
            ))}
          </Select>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <DatePicker
              label="От"
              value={startDate}
              onChange={(newDate) => setStartDate(newDate)}
              sx={{ width: 150 }}
              renderInput={(params) => <TextField {...params} fullWidth margin="dense" />}
            />
            {/* <EventIcon fontSize="small" color="action" /> */}
            <DatePicker
              label="До"
              value={endDate}
              onChange={(newDate) => setEndDate(newDate)}
              sx={{ width: 150 }}
              renderInput={(params) => <TextField {...params} fullWidth margin="dense" />}
            />
          </Stack>
        </Stack>
        <div style={{ flex: 1, minHeight: 300 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : analyticsData.length > 0 ? (
            <LineChart
              skipAnimation
              dataset={analyticsData}
              series={selectedData.series.map((s, i) => ({
                id: s.id,
                dataKey: s.dataKey,
                stack: 'v',
                area: true,
                curve: 'linear',
                label: s.label,
                valueFormatter: (value) => (value !== null ? IntlAbsolute.format(value) : null),
                color: blueberryTwilightPalette('light')[i] || s.color || theme.palette.primary.main
              }))}
              xAxis={[
                {
                  dataKey: 'date',
                  scaleType: 'band',
                  domainLimit: 'strict',
                  zoom: true,
                  height: 30,
                  tickNumber: 5,
                  // valueFormatter: (value) => shortMonthYearFormatter(new Date(value)),
                },
              ]}
              yAxis={[
                {
                  min: 0,
                  zoom: true,
                  valueFormatter: IntlNumber.format,
                },
              ]}
              margin={{ left: 0, bottom: 0 }}
              sx={{
                [`& .${lineElementClasses.line}`]: {
                  strokeWidth: 1.5,
                },
              }}
            >
              {/* {selectedData.series.map((s, i) => (
                <AreaGradient color={blueberryTwilightPalette('light')[i]} id={s.id} key={s.id} />
              ))} */}
            </LineChart>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary' }}>
              <Typography variant="body2">Нет данных за выбранный период</Typography>
            </Box>
          )}
        </div>
      </Stack>
    );
  }

  /** */

  function PieChartDemo() {
    const versionMap = {
      '5': 'v5',
      '6': 'v6',
      '7': 'v7',
    };

    const generateDataset = (data) => {
      const index = data.timestamps.length - 1;
      const rep = { date: new Date(data.timestamps[index]) };
      Object.entries(data.downloads).forEach(([version, values]) => {
        const major = version.split('.')[0];
        rep[major] = (rep[major] ?? 0) + values[index];
      });
      return rep;
    }

    const dateEntry = generateDataset(dataMaterial);
    const data = dateEntry ? (Object.keys(versionMap)).map((key) => ({ label: versionMap[key], value: dateEntry[key] ?? 0, })) : [];

    return (
      <React.Fragment>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, px: 1 }}>
          @mui/material downloads on{' '}
          {dateEntry.date.toLocaleString('en-US', {
            month: 'short',
            day: '2-digit',
            year: '2-digit',
          })}
        </Typography>
        <PieChart
          series={[
            {
              startAngle: -90,
              endAngle: 90,
              data,
              innerRadius: '150%',
              outerRadius: '200%',
              cy: '100%',
            },
          ]}
          sx={{ aspectRatio: 'auto' }}
          height={140}
          margin={{
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
          slotProps={{
            legend: {
              direction: 'row',
              position: {
                vertical: 'bottom',
                horizontal: 'center',
              },
            },
          }}
        />
      </React.Fragment>
    );
  }

  /** */

  function BarChartDemo() {
    const dataset = [
      { weekDay: 'Monday', value: 400 },
      { weekDay: 'Tuesday', value: 300 },
      { weekDay: 'Wednesday', value: 200 },
      { weekDay: 'Thursday', value: 500 },
      { weekDay: 'Friday', value: 400 },
      { weekDay: 'Saturday', value: 100 },
      { weekDay: 'Sunday', value: 50 },
    ];

    const valueFormatter = (value, data = {}) => {
      if (data?.location === 'tick') return value[0];
      return value;
    };

    return (
      <React.Fragment>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, px: 1 }}>
          Issues opened daily
        </Typography>
        <BarChart
          dataset={dataset}
          colors={['#4254FB']}
          series={[{ dataKey: 'value' }]}
          margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
          height={100}
          borderRadius={4}
          xAxis={[{ dataKey: 'weekDay', scaleType: 'band', valueFormatter, disableTicks: true }]}
          yAxis={[{ position: 'left' }]}
        />
      </React.Fragment>
    );
  }

  /** */

  const numberFormatter = new Intl.NumberFormat('en-US').format;

  const TooltipPaper = styled('div', {
    name: 'Tooltip',
    slot: 'Paper',
  })(({ theme }) => {
    return {
      padding: theme.spacing(1),
      backgroundColor: (theme.vars || theme).palette.background.paper,
      color: (theme.vars || theme).palette.text.primary,
      borderRadius: (theme.vars || theme).shape?.borderRadius,
      border: `solid ${(theme.vars || theme).palette.divider} 1px`,
    };
  });

  function CustomTooltip({ itemData }) {
    if (!itemData) return null;

    return (
      <TooltipPaper>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: itemData.color,
              borderRadius: 1,
              mr: 2,
            }}
          />
          <Typography>{itemData.label}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ mr: 3 }}>{itemData.value.x}</Typography>
          <Typography>
            {itemData.value.y == null
              ? 'NaN'
              : `${numberFormatter(itemData.value.y)} transistor/mm²`}
          </Typography>
        </Box>
      </TooltipPaper>
    );
  }

  // function CustomTooltip() {
  //   const item = useItemTooltip();

  //   return (
  //     <ChartsTooltip trigger="item">
  //       <TooltipPaper>
  //         <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
  //           <Box
  //             sx={{
  //               width: 20,
  //               height: 20,
  //               backgroundColor: item?.color,
  //               borderRadius: 1,
  //               mr: 2,
  //             }}
  //           />
  //           <Typography>{item?.label}</Typography>
  //         </Box>
  //         <Divider sx={{ my: 1 }} />
  //         <Box
  //           sx={{
  //             display: 'flex',
  //             flexDirection: 'row',
  //             justifyContent: 'space-between',
  //           }}
  //         >
  //           <Typography sx={{ mr: 3 }}>{item?.value.x}</Typography>
  //           <Typography>
  //             {item?.value.y == null
  //               ? 'NaN'
  //               : `${numberFormatter(item?.value.y)} transistor/mm²`}
  //           </Typography>
  //         </Box>
  //       </TooltipPaper>
  //     </ChartsTooltip>
  //   );
  // }

  function ScatterChartDemo() {
    const chartSetting = {
      yAxis: [{ width: 50, scaleType: 'log' }],
      xAxis: [{ valueFormatter: (v) => (v ? v.toString() : '') }],
    };

    const constructors = ['Intel', 'Apple', 'AMD'];

    const series = [
      {
        type: 'scatter',
        label: 'Other',
        highlightScope: { highlight: 'series', fade: 'global' },
        markerSize: 3,
        data: transistorCPUdata.filter((item) => !constructors.includes(item.constructor) && item.density !== null).map((item) => ({ x: item.year, y: item.density, id: item.id })),
      },
      ...constructors.map((constructor) => ({
        label: constructor,
        highlightScope: { highlight: 'series', fade: 'global' },
        markerSize: 3,
        data: transistorCPUdata.filter((item) => item.constructor === constructor && item.density !== null).map((item) => ({ x: item.year, y: item.density, id: item.id })),
      })),
    ];
    return (
      <Stack sx={{ width: '100%' }}>
        <Typography align="center">Processor density (in transistor/mm²)</Typography>
        <ScatterChart
          height={150}
          series={series}
          grid={{ horizontal: true, vertical: true }}
          hitAreaRadius={20}
          slots={{ tooltip: CustomTooltip }}
          {...chartSetting}
        />
      </Stack>
    );
  }

  // Компонент статистической карточки
  const StatCard = ({ title, value, icon, color }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'visible'
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: isMobile ? 1 : 2
        }}>
          <Avatar
            sx={{
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              mb: isMobile ? 1 : 0
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ ml: isMobile ? 0 : 2 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="div"
              sx={{ fontWeight: 'bold' }}
            >
              {loading ? <Skeleton width={60} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ruLocale}>
      <Box>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          gutterBottom
          sx={{
            mb: { xs: 2, sm: 3, md: 4 },
            fontWeight: 'bold',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}>
          Дашборд
        </Typography>

        <Paper
          component="div"
          variant="outlined"
          sx={(theme) => ({
            mb: 4,
            mx: 0,
            maxWidth: '100%',
            height: { md: 640 },
            overflow: 'hidden',
            p: 1,
            // background: 'linear-gradient(to right, #f5f7fa, #c3cfe2)',
          })}>

          <Stack direction="row" spacing={1} sx={{ height: '100%' }}>
            {/* Left/Main Section */}
            <Stack
              direction="column"
              spacing={1}
              sx={{
                flexBasis: { xs: '100%', md: '65%' },
                maxHeight: { xs: 500, md: 580 },
                minWidth: 0,
                pr: { md: 1, xs: 0 },
              }}>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ md: 1, xs: 0.5 }} sx={{ display: { xs: 'none', md: 'flex' } }}            >
                {statCardData.map((card, index) => ( // верхние карточки
                  <Card key={card.title} variant="outlined" sx={{ height: 'fit-content', flexBasis: '33.3%' }}>
                    <CardContent sx={{ padding: { xs: 1, md: 1.5 }, '&:last-child': { paddingBottom: { xs: 1, md: 1.5 } } }}                  >
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {card.title}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-end' }}>
                        <Typography variant="h4" component="p" sx={{ fontSize: '1.5rem', fontWeight: 500 }}>
                          {card.value}
                        </Typography>
                        <Box sx={{ width: '100%', height: 50 }}>

                          <SparkLineChart
                            data={card.data}
                            colors={['#4254FB']}
                            area
                            showHighlight
                            showTooltip
                            slotProps={{
                              area: { sx: { fill: `url(#area-gradient-${card.value})` } }
                            }}>
                            <defs>
                              <linearGradient id={`area-gradient-${card.value}`} x1="50%" y1="0%" x2="50%" y2="100%">
                                <stop offset="0%" stopColor="#4254FB" stopOpacity={0.3} />
                                <stop offset="100%" stopColor="#4254FB" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                          </SparkLineChart>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
              <Box component="div" sx={{ flexGrow: 1, minHeight: 0 }}>
                <DownloadDemo />
              </Box>
            </Stack>
            {/* Right/Sidebar Section */}
            <Stack
              direction="column"
              spacing={1}
              sx={{ flexBasis: { xs: '100%', md: '35%' }, display: { xs: 'none', md: 'flex' } }}
            >
              <Paper
                component="div"
                variant="outlined"
                sx={{ px: 1, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <PieChartDemo />
              </Paper>
              <Paper
                component="div"
                variant="outlined"
                sx={{ px: 1, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <BarChartDemo />
              </Paper>
              <Paper component="div" variant="outlined" sx={{ px: 1, py: 2 }}>
                {/* <HeatmapDemo /> */}
                <ScatterChartDemo />
              </Paper>
            </Stack>
          </Stack>
        </Paper>

        {/* Статистические карточки */}
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Всего пользователей"
              value={stats.totalUsers}
              icon={<PeopleIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Новые пользователи"
              value={stats.newUsers}
              icon={<PersonAddIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Заблокированные"
              value={stats.blockedUsers}
              icon={<BlockIcon />}
              color="error"
            />
          </Grid>
        </Grid>

        {/* Методы аутентификации */}
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="Email авторизация"
              value={stats.emailUsers}
              icon={<EmailIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="SMS авторизация"
              value={stats.phoneUsers}
              icon={<PhoneIcon />}
              color="warning"
            />
          </Grid>
        </Grid>

        {/* Последние пользователи */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Последние пользователи
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            // Скелетон загрузки
            Array.from(new Array(5)).map((_, index) => (
              <Box key={index} sx={{ display: 'flex', my: 2 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ width: '100%' }}>
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </Box>
              </Box>
            ))
          ) : (
            // Список пользователей
            <List sx={{ width: '100%', p: 0 }}>
              {recentUsers.length > 0 ? (
                recentUsers.map((user) => (
                  <ListItem key={user._id} alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: user.isBlocked ? 'error.main' : 'primary.main' }}>
                        {user.authMethod === 'email' ? <EmailIcon /> : <PhoneIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.email || user.phone}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {user.authMethod === 'email' ? 'Email' : 'Телефон'}
                          </Typography>
                          {' — '}
                          {user.isBlocked ? 'Заблокирован' : 'Активен'}
                          {' • '}
                          {new Date(user.createdAt).toLocaleDateString()}
                        </>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Нет данных о пользователях
                </Typography>
              )}
            </List>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;



// {{"component": "modules/components/overview/charts/mainDemo/MainDemo"}}